import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import subscriptionService from '../../services/SubscriptionService';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySession = async () => {
      try {
        // Get the session_id from URL query params
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session_id');
        
        if (!sessionId) {
          setError('Missing session ID. Cannot verify payment.');
          setIsVerifying(false);
          return;
        }
        
        // Verify the checkout session with the backend
        const result = await subscriptionService.verifyCheckoutSession(sessionId);
        
        if (result.success) {
          setSuccess(true);
        } else {
          setError('Payment verification failed. Please contact support.');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('An error occurred while verifying your payment. Please contact support.');
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifySession();
  }, [location.search]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          width: '100%', 
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        {isVerifying ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={60} />
            <Typography variant="h6">Verifying your payment...</Typography>
          </Box>
        ) : success ? (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Payment Successful</Typography>
            <Typography variant="body1" paragraph>
              Thank you for your subscription! Your account has been successfully updated.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Return to Dashboard
            </Button>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>{error || 'Payment verification failed'}</Alert>
            <Typography variant="body1" paragraph>
              We encountered an issue with your payment verification. Please contact support or try again.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/organization/subscription')}
              >
                Try Again
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;
